import {ensureRichText, isEmptyRichText, richTextToPlain} from '../form/richText.js';

// App-agnostic requirement authoring helpers — the form ↔ payload shape, step/
// question factories, and label maps. Shared by the embertracking app and the hub
// via @embertracking/ui/requirements. I/O (Firestore writes, callFunction, file
// uploads) lives in each app's own data layer; everything here is pure.

export {ensureRichText, isEmptyRichText, richTextToPlain};

export const STEP_TYPES = {
    DOCUMENT: {label: 'Document', description: 'Upload a PDF or image to read and acknowledge'},
    PAGE: {label: 'Page', description: 'Rich-text page to read'},
    VIDEO: {label: 'Video', description: 'Watch a video, then mark it complete'},
    QUIZ: {label: 'Quiz', description: 'Multiple-choice questions with a passing score'},
    EXTERNAL: {label: 'External link', description: 'Link out to complete a qualification on another site'},
    FILE_UPLOAD: {label: 'File upload', description: 'Ask the member to upload a file'}
};

// A quiz question. `questionType` is 'single' (one correct answer, stored as an
// index) or 'multi' (one or more correct answers, stored as a sorted index array).
// The field was stored from the MVP on so multi-select needs no data migration.
export const makeQuestion = () => ({questionType: 'single', question: '', options: ['', ''], correctAnswer: 0, explanation: ''});

// Coerce a question's correctAnswer into the shape its questionType expects: a
// sorted unique index array for 'multi', a single index for 'single'.
export const normalizeCorrectAnswer = (questionType, correctAnswer, optionCount = Infinity) => {
    const valid = v => Number.isInteger(v) && v >= 0 && v < optionCount;
    if (questionType === 'multi') {
        const arr = Array.isArray(correctAnswer) ? correctAnswer : (Number.isInteger(correctAnswer) ? [correctAnswer] : []);
        const cleaned = [...new Set(arr.filter(valid))].sort((a, b) => a - b);
        return cleaned.length ? cleaned : (optionCount > 0 ? [0] : []);
    }
    const single = Array.isArray(correctAnswer) ? correctAnswer[0] : correctAnswer;
    return valid(single) ? single : 0;
};

export const STATUS_LABELS = {
    DRAFT: 'Draft',
    PUBLISHED: 'Published',
    ARCHIVED: 'Archived'
};

export const ASSIGNMENT_STATUS_LABELS = {
    ASSIGNED: 'Not started',
    IN_PROGRESS: 'In progress',
    PENDING_VALIDATION: 'Pending validation',
    COMPLETE: 'Complete',
    EXPIRED: 'Expired',
    CANCELLED: 'Cancelled'
};

// One entry per stored qualification path, labelled where this provider presents it.
// Storage-only cells such as `shared.hazmatOps` are reached through a level alias,
// so users see "Full Service — HAZMAT Operations" rather than the internal category.
// Canonical categories come first in each catalogue; keeping the first occurrence also
// prevents COTR's display aliases from duplicating Provincial Requirements in pickers.
export const qualificationSkillLabels = skills => {
    const labels = new Map();
    for (const [level, category] of Object.entries(skills || {})) {
        if (category?.storageOnly) continue;
        const levelLabel = category?.label ?? level;
        for (const [key, skill] of Object.entries(category?.skills || {})) {
            const config = skill && typeof skill === 'object' ? skill : {label: skill};
            const path = config.skill || `${level}.${key}`;
            if (labels.has(path)) continue;
            const skillLabel = config.label || key;
            labels.set(path, [levelLabel, skillLabel].filter(Boolean).join(' — '));
        }
    }
    return labels;
};

export const qualificationSkillOptions = skills => [...qualificationSkillLabels(skills)]
    .map(([value, label]) => ({value, label}));

const EMPTY_PAGE = {type: 'doc', content: [{type: 'paragraph'}]};

let stepCounter = 0;
// Stable-ish unique step id. Math.random/Date are fine in the browser; the id
// only needs to be unique within a single requirement's step array.
export const newStepId = () => {
    stepCounter += 1;
    return `step_${Date.now().toString(36)}_${stepCounter}_${Math.floor(Math.random() * 1e6).toString(36)}`;
};

export const makeStep = type => {
    const id = newStepId();
    if (type === 'DOCUMENT') {
        return {id, type: 'DOCUMENT', name: '', files: [], requireAcknowledge: true};
    }
    if (type === 'VIDEO') {
        return {id, type: 'VIDEO', name: '', videoUrl: '', files: [], minWatchSeconds: '', requireAcknowledge: false};
    }
    if (type === 'QUIZ') {
        return {id, type: 'QUIZ', name: '', passingScore: 80, retakesAllowed: '', questions: [makeQuestion()]};
    }
    if (type === 'EXTERNAL') {
        return {id, type: 'EXTERNAL', name: '', url: '', body: EMPTY_PAGE};
    }
    if (type === 'FILE_UPLOAD') {
        return {id, type: 'FILE_UPLOAD', name: '', body: EMPTY_PAGE};
    }
    return {id, type: 'PAGE', name: '', body: EMPTY_PAGE, requireAcknowledge: false};
};

// Normalise a raw requirement doc into the shape react-hook-form expects.
export const prepareRequirementForForm = requirement => {
    if (!requirement) return requirement;
    const steps = Array.isArray(requirement.steps) ? requirement.steps.map(step => {
        if (step.type === 'PAGE') return {...step, body: ensureRichText(step.body)};
        if (step.type === 'VIDEO') {
            return {...step, videoUrl: step.videoUrl || '', files: Array.isArray(step.files) ? step.files : [], minWatchSeconds: step.minWatchSeconds ?? '', requireAcknowledge: step.requireAcknowledge === true};
        }
        if (step.type === 'QUIZ') {
            const questions = Array.isArray(step.questions) && step.questions.length
                ? step.questions.map(q => {
                    const questionType = q.questionType === 'multi' ? 'multi' : 'single';
                    const options = Array.isArray(q.options) && q.options.length >= 2 ? q.options.map(o => o ?? '') : ['', ''];
                    return {
                        questionType,
                        question: q.question || '',
                        options,
                        correctAnswer: normalizeCorrectAnswer(questionType, q.correctAnswer, options.length),
                        explanation: q.explanation || ''
                    };
                })
                : [makeQuestion()];
            return {...step, passingScore: step.passingScore ?? '', retakesAllowed: step.retakesAllowed ?? '', questions};
        }
        if (step.type === 'EXTERNAL') {
            return {...step, url: step.url || '', body: ensureRichText(step.body)};
        }
        if (step.type === 'FILE_UPLOAD') {
            return {...step, body: ensureRichText(step.body)};
        }
        return {...step, files: Array.isArray(step.files) ? step.files : []};
    }) : [];
    return {
        name: requirement.name || '',
        description: ensureRichText(requirement.description),
        category: requirement.category || '',
        completionWindowDays: requirement.completionWindowDays ?? '',
        validForMonths: requirement.validForMonths ?? '',
        autoReassign: requirement.autoReassign === true,
        requireValidation: requirement.requireValidation === true,
        awardSkill: requirement.awardSkill?.level && requirement.awardSkill?.key ? `${requirement.awardSkill.level}.${requirement.awardSkill.key}` : '',
        steps
    };
};

// Awards are addressed as `level.key` (matches the qualification matrix). The
// authoring autocomplete stores that string; persist it as {level, key}.
const parseAwardSkill = value => {
    if (!value || typeof value !== 'string') return null;
    const i = value.indexOf('.');
    if (i < 1 || i === value.length - 1) return null;
    return {level: value.slice(0, i), key: value.slice(i + 1)};
};

const toNumberOrNull = value => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

// Validation is a live setting. A settings-only save must preserve any existing
// content draft and its unpublished-changes marker.
export const requirementSettingsOnly = dirtyFields => {
    const fields = Object.keys(dirtyFields);
    return fields.length > 0 && fields.every(field => field === 'requireValidation');
};

export const requirementSavePayload = (data, {uid, settingsOnly = false} = {}) => settingsOnly ? {
    requireValidation: data.requireValidation === true,
    updatedAt: new Date(),
    updatedBy: uid || null
} : requirementFormToPayload(data, {uid});

// Build the persisted draft payload from form values.
export const requirementFormToPayload = (data, {uid} = {}) => ({
    name: (data.name || '').trim(),
    description: ensureRichText(data.description),
    category: (data.category || '').trim() || null,
    completionWindowDays: toNumberOrNull(data.completionWindowDays),
    validForMonths: toNumberOrNull(data.validForMonths),
    autoReassign: data.autoReassign === true,
    requireValidation: data.requireValidation === true,
    awardSkill: parseAwardSkill(data.awardSkill),
    steps: (data.steps || []).map(step => {
        if (step.type === 'PAGE') {
            return {id: step.id, type: 'PAGE', name: step.name || '', body: ensureRichText(step.body), requireAcknowledge: step.requireAcknowledge === true};
        }
        if (step.type === 'VIDEO') {
            const files = (step.files || []).map(f => ({filePath: f.filePath, name: f.name || null, mimeType: f.mimeType || null}));
            return {
                id: step.id,
                type: 'VIDEO',
                name: step.name || '',
                // An uploaded file wins over a pasted URL.
                videoUrl: files.length ? '' : (step.videoUrl || '').trim(),
                files,
                minWatchSeconds: toNumberOrNull(step.minWatchSeconds),
                requireAcknowledge: step.requireAcknowledge === true
            };
        }
        if (step.type === 'QUIZ') {
            return {
                id: step.id,
                type: 'QUIZ',
                name: step.name || '',
                passingScore: toNumberOrNull(step.passingScore) ?? 80,
                retakesAllowed: toNumberOrNull(step.retakesAllowed),
                questions: (step.questions || []).map(q => {
                    const options = (Array.isArray(q.options) ? q.options : []).map(o => (o || '').trim());
                    const questionType = q.questionType === 'multi' ? 'multi' : 'single';
                    return {
                        questionType,
                        question: (q.question || '').trim(),
                        options,
                        correctAnswer: normalizeCorrectAnswer(questionType, q.correctAnswer, options.length),
                        explanation: (q.explanation || '').trim() || null
                    };
                })
            };
        }
        if (step.type === 'EXTERNAL') {
            return {
                id: step.id,
                type: 'EXTERNAL',
                name: step.name || '',
                url: (step.url || '').trim(),
                body: ensureRichText(step.body)
            };
        }
        if (step.type === 'FILE_UPLOAD') {
            return {
                id: step.id,
                type: 'FILE_UPLOAD',
                name: step.name || '',
                body: ensureRichText(step.body)
            };
        }
        return {
            id: step.id,
            type: 'DOCUMENT',
            name: step.name || '',
            requireAcknowledge: step.requireAcknowledge !== false,
            files: (step.files || []).map(f => ({filePath: f.filePath, name: f.name || null, mimeType: f.mimeType || null}))
        };
    }),
    hasDraftChanges: true,
    updatedAt: new Date(),
    updatedBy: uid || null
});
